import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import { useState, useEffect } from 'react';
import {
  Box,
  ExternalLink,
  Settings,
  Edit2,
  RefreshCw,
  X,
  Globe,
  Palette,
  Users,
  ChevronRight,
  Plus,
  Trash2,
  Package,
  Check,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import { StaffPage } from '../components/staff/StaffChrome';
import { resolveAccent, tint } from '../utils/accentColor';
import { api } from '../services/apiService';
import { loadingService } from '../services/loadingService';
import { toastService } from '../services/toastService';

interface Product {
  id: string;
  name: string;
  description: string;
  domain: string;
  devDomain?: string;
  appDomain?: string;
  devAppDomain?: string;
  status: 'active' | 'coming_soon' | 'deprecated';
  theme: {
    primaryColor: string;
    secondaryColor: string;
  };
  defaultPackages: string[];
  availablePackages: string[];
  features?: string[];
  defaultRoute?: string;
  createdAt: number;
  updatedAt: number;
}

interface PackageItem {
  id: string;
  name: string;
  description: string;
  features: string[];
  isCore?: boolean;
  /**
   * Read-only here. Owned by `/packages`, which is the one place it can be
   * changed; this page only shows it so the choice is made with open eyes.
   */
  marketplace?: boolean;
  /** List price in integer CENTS, from the packages table. Absent = unpriced. */
  basePrice?: number;
}

/**
 * WHAT A PRODUCT'S PACKAGES LIST AT, PER MONTH.
 *
 * A product IS its package set, so its monthly value is the sum of what those
 * packages list at — the same figure a deal on this product starts from before
 * any discount. It is stated here so a super admin picking packages can see the
 * price they are assembling rather than discovering it in the deal builder.
 *
 * A package with NO numeric `basePrice` is counted as unpriced and named, never
 * silently added as $0: "the total is $154.90" and "the total is $154.90 plus
 * two packages nobody has priced" are different facts.
 */
function packageMonthlyTotal(
  packageIds: string[],
  packages: PackageItem[],
): { cents: number; unpricedCount: number; missingCount: number } {
  const byId = new Map(packages.filter((p) => p?.id).map((p) => [p.id, p]));
  let cents = 0;
  let unpricedCount = 0;
  let missingCount = 0;
  for (const id of Array.from(new Set(packageIds || []))) {
    const pkg = byId.get(id);
    if (!pkg) { missingCount += 1; continue; }
    if (typeof pkg.basePrice !== 'number') { unpricedCount += 1; continue; }
    cents += Math.max(0, Math.round(pkg.basePrice));
  }
  return { cents, unpricedCount, missingCount };
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusBadges: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-800' },
  coming_soon: { label: 'Coming Soon', className: 'bg-amber-100 text-amber-800' },
  deprecated: { label: 'Deprecated', className: 'bg-red-100 text-red-800' },
};

interface ProductFormData {
  id: string;
  name: string;
  description: string;
  domain: string;
  primaryColor: string;
  secondaryColor: string;
  status: 'active' | 'coming_soon' | 'deprecated';
  defaultPackages: string[];
  availablePackages: string[];
}

const initialFormData: ProductFormData = {
  id: '',
  name: '',
  description: '',
  domain: '',
  primaryColor: '#3B82F6',
  secondaryColor: '#60A5FA',
  status: 'coming_soon',
  defaultPackages: [],
  availablePackages: [],
};

function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<{ productId: string; message: string; deals?: any[] } | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [productsRes, packagesRes] = await Promise.all([
        api.packages.listProducts(),
        api.packages.listVisible(),
      ]);
      setProducts((productsRes.products || []).map((p: any) => ({
        ...p,
        theme: p.theme || { primaryColor: '#3B82F6', secondaryColor: '#60A5FA' },
      })));
      setPackages(packagesRes.packages || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toastService.error('Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    loadingService.start();
    fetchData().finally(() => loadingService.stop());
    toastService.info('Refreshing products...');
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedProduct(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleOpenEdit = (product: Product) => {
    setIsEditing(true);
    setSelectedProduct(product);
    setFormData({
      id: product.id,
      name: product.name,
      description: product.description,
      domain: product.domain,
      primaryColor: product.theme.primaryColor,
      secondaryColor: product.theme.secondaryColor,
      status: product.status,
      defaultPackages: product.defaultPackages || [],
      availablePackages: product.availablePackages || [],
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.domain) {
      toastService.error('Name and domain are required');
      return;
    }

    if (!isEditing && !formData.id) {
      toastService.error('Product ID is required');
      return;
    }

    loadingService.start();
    try {
      const payload = {
        id: formData.id,
        name: formData.name,
        description: formData.description,
        domain: formData.domain,
        theme: {
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
        },
        status: formData.status,
        defaultPackages: formData.defaultPackages,
        availablePackages: formData.availablePackages,
        syncTenants: true, // Auto-sync packages to existing tenants
      };

      console.log('[ProductsPage] Saving product with payload:', JSON.stringify(payload, null, 2));

      if (isEditing && selectedProduct) {
        const result = await api.packages.updateProduct(selectedProduct.id, payload);
        console.log('[ProductsPage] Update result:', result);
        const syncCount = (result as any)?.tenantsSynced || 0;
        if (syncCount > 0) {
          toastService.success(`Product "${formData.name}" updated - ${syncCount} tenant(s) synced`);
        } else {
          toastService.success(`Product "${formData.name}" updated successfully`);
        }
      } else {
        await api.packages.createProduct(payload);
        toastService.success(`Product "${formData.name}" created successfully`);
      }

      handleCloseModal();
      fetchData();
    } catch (error: any) {
      console.error('Failed to save product:', error);
      const message = error?.message || 'Failed to save product';
      toastService.error(message);
    } finally {
      loadingService.stop();
    }
  };

  const handleDelete = async (productId: string) => {
    loadingService.start();
    try {
      await api.packages.deleteProduct(productId);
      toastService.success('Product deleted successfully');
      setDeleteConfirm(null);
      setDeleteError(null);
      fetchData();
    } catch (error: any) {
      console.error('Failed to delete product:', error);
      // Check if it's a 409 conflict (product used by deals)
      if (error?.status === 409 || error?.message?.includes('used by deals')) {
        const errorData = error?.data || {};
        setDeleteError({
          productId,
          message: errorData.error || 'Cannot delete product that is used by deals',
          deals: errorData.deals || [],
        });
        setDeleteConfirm(null);
      } else {
        toastService.error(error?.message || 'Failed to delete product');
      }
    } finally {
      loadingService.stop();
    }
  };

  // Simplified: checking a package adds it to both default and available
  // All assigned packages are automatically synced to tenants
  const togglePackageSelection = (packageId: string) => {
    setFormData((prev) => {
      const isCurrentlySelected = prev.defaultPackages.includes(packageId);
      if (isCurrentlySelected) {
        // Remove from both
        return {
          ...prev,
          defaultPackages: prev.defaultPackages.filter((id) => id !== packageId),
          availablePackages: prev.availablePackages.filter((id) => id !== packageId),
        };
      } else {
        // Add to both
        return {
          ...prev,
          defaultPackages: [...prev.defaultPackages, packageId],
          availablePackages: [...prev.availablePackages, packageId],
        };
      }
    });
  };

  const getPackageName = (packageId: string) => {
    const pkg = packages.find((p) => p.id === packageId);
    return pkg?.name || packageId;
  };

  return (
    <StaffPage
      icon={Box}
      section="Platform"
      title="Products"
      subtitle={`${products.length} product${products.length === 1 ? '' : 's'} \u00b7 domains, themes and package bundles`}
      actions={(
        <>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors disabled:opacity-50 min-h-[40px]"
          >
            {isLoading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 min-h-[40px] text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create Product</span>
          </button>
        </>
      )}
    >

      {/* Loading State */}
      {isLoading && products.length === 0 && (
        <RubyLoader variant="page" label="Loading products..." />
      )}

      {/* Empty State */}
      {!isLoading && products.length === 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <Box className="w-12 h-12 text-stone-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Yet</h3>
          <p className="text-stone-600 mb-6">Create your first product to get started.</p>
          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-admin-primary hover:bg-admin-secondary text-white font-medium rounded-lg transition-colors"
          >
            Create Product
          </button>
        </div>
      )}

      {/* Products Grid */}
      {products.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl border border-stone-200 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_36px_-12px_var(--prod-glow)]"
              style={{ ['--prod-glow' as any]: tint(product.theme.primaryColor, 0.55) }}
            >
              {/* Product Header — painted in the product's OWN theme colours at
                  full strength (they ARE the product's identity), white body
                  below. Ink is derived, never assumed: a product themed pale
                  yellow would otherwise render white-on-white. */}
              {(() => {
                const a = resolveAccent({
                  colorType: 'gradient',
                  gradientFrom: product.theme.primaryColor,
                  gradientTo: product.theme.secondaryColor,
                });
                return (
                  <div className="relative px-6 py-5 overflow-hidden" style={{ background: a.band }}>
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{ backgroundImage: `radial-gradient(160px 100px at 88% -30%, ${tint(a.ink, 0.35)}, transparent 70%)` }}
                    />
                    <Box
                      className="absolute -right-5 -bottom-7 w-32 h-32 pointer-events-none transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6"
                      style={{ color: a.ink, opacity: 0.13 }}
                    />
                    <div className="relative flex items-center gap-4">
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 backdrop-blur-sm"
                        style={{ background: tint(a.ink, 0.18), border: `1px solid ${tint(a.ink, 0.3)}` }}
                      >
                        <Box className="w-7 h-7" style={{ color: a.ink }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold truncate" style={{ color: a.ink }}>{product.name}</h3>
                        <a
                          href={`https://${product.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm flex items-center gap-1 transition-opacity hover:opacity-100 truncate mt-0.5"
                          style={{ color: a.ink, opacity: 0.8 }}
                        >
                          {product.domain}
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Product Body */}
              <div className="p-6">
                <p className="text-stone-600 text-sm mb-4 line-clamp-2">{product.description || 'No description'}</p>

                {/* Status */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`text-xs px-2.5 py-1 rounded font-medium ${
                      statusBadges[product.status]?.className || 'bg-stone-200 text-stone-700'
                    }`}
                  >
                    {statusBadges[product.status]?.label || product.status}
                  </span>
                  <span className="text-xs text-stone-500 font-mono">{product.id}</span>
                </div>

                {/* Packages Summary */}
                <div className="mb-4">
                  <p className="text-xs text-stone-500 mb-2 flex items-center gap-1 flex-wrap">
                    <Package className="w-3 h-3" />
                    Packages ({product.availablePackages?.length || 0} available, {product.defaultPackages?.length || 0} default)
                    {/* What the default set LISTS at per month — the figure a
                        deal on this product starts from, readable without
                        opening the editor. */}
                    {(product.defaultPackages?.length || 0) > 0 && (() => {
                      const total = packageMonthlyTotal(product.defaultPackages, packages);
                      return (
                        <span className="ml-auto font-bold tabular-nums text-gray-900">
                          {money(total.cents)}<span className="font-normal text-stone-500">/mo</span>
                        </span>
                      );
                    })()}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(product.defaultPackages || []).slice(0, 3).map((pkgId) => (
                      <span
                        key={pkgId}
                        className="text-[11px] px-2 py-1 rounded-md font-medium"
                        style={{
                          background: tint(product.theme.primaryColor, 0.12),
                          color: resolveAccent({ color: product.theme.primaryColor }).onWhite,
                        }}
                      >
                        {getPackageName(pkgId)}
                      </span>
                    ))}
                    {(product.defaultPackages?.length || 0) > 3 && (
                      <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-600 rounded">
                        +{product.defaultPackages.length - 3}
                      </span>
                    )}
                    {(product.defaultPackages?.length || 0) === 0 && (
                      <span className="text-xs text-stone-500 italic">No default packages</span>
                    )}
                  </div>
                </div>

                {/* Theme Colors */}
                <div className="mb-4">
                  <p className="text-xs text-stone-500 mb-2">Theme:</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border border-stone-300"
                        style={{ backgroundColor: product.theme.primaryColor }}
                        title="Primary"
                      />
                      <div
                        className="w-6 h-6 rounded border border-stone-300"
                        style={{ backgroundColor: product.theme.secondaryColor }}
                        title="Secondary"
                      />
                    </div>
                    <span className="text-xs text-stone-500 font-mono">
                      {product.theme.primaryColor}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-stone-200 flex items-center justify-between">
                  <button
                    onClick={() => handleOpenEdit(product)}
                    className="flex items-center gap-2 text-sm text-admin-primary hover:text-admin-secondary transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setDeleteConfirm(product.id)}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <a
                      href={`https://${product.appDomain || `app.${product.domain}`}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                    >
                      Open App
                      <ChevronRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-2">About Products</h3>
        <p className="text-stone-600 text-sm">
          Products are the different SaaS applications running on the platform. Each product
          has its own domain, theme, and set of packages. Configure which packages are available
          for a product and which ones are assigned by default when a new tenant signs up.
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-stone-200 p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Product?</h3>
            <p className="text-stone-600 text-sm mb-6">
              Are you sure you want to delete this product? This action cannot be undone.
              Products that are assigned to deals cannot be deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-gray-900 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Error Modal */}
      {deleteError && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-stone-200 p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cannot Delete Product</h3>
            </div>
            <p className="text-stone-600 text-sm mb-4">{deleteError.message}</p>
            {deleteError.deals && deleteError.deals.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-stone-500 mb-2">Used by these deals:</p>
                <div className="bg-stone-100 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {deleteError.deals.map((deal: any, idx: number) => (
                    <div key={idx} className="text-sm text-stone-600 py-1">
                      {deal.name || deal.code} <span className="text-stone-500">({deal.code})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-stone-500 mb-4">
              Remove this product from the deals above before deleting.
            </p>
            <button
              onClick={() => setDeleteError(null)}
              className="w-full px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-gray-900 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl border border-stone-200 w-full sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Product' : 'Create Product'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Product Details */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Product Details
                  </h3>

                  {/* Product Preview */}
                  <div
                    className="p-4 rounded-lg"
                    style={{
                      background: `linear-gradient(135deg, ${formData.primaryColor}20, ${formData.secondaryColor}20)`,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${formData.primaryColor}30` }}
                      >
                        <Box className="w-6 h-6" style={{ color: formData.primaryColor }} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{formData.name || 'Product Name'}</p>
                        <p className="text-sm text-stone-600">{formData.domain || 'domain.com'}</p>
                      </div>
                    </div>
                  </div>

                  {!isEditing && (
                    <div>
                      <label className="block text-sm font-medium text-stone-600 mb-1.5">
                        Product ID *
                      </label>
                      <input
                        type="text"
                        value={formData.id}
                        onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="my-product"
                        className="w-full px-4 py-2.5 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 placeholder:text-stone-500 focus:outline-none focus:border-admin-primary font-mono"
                        required
                      />
                      <p className="text-xs text-stone-500 mt-1">Lowercase letters, numbers, and hyphens only</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 placeholder:text-stone-500 focus:outline-none focus:border-admin-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2.5 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 placeholder:text-stone-500 focus:outline-none focus:border-admin-primary resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Domain *
                    </label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      placeholder="example.com"
                      className="w-full px-4 py-2.5 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 placeholder:text-stone-500 focus:outline-none focus:border-admin-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as 'active' | 'coming_soon' | 'deprecated',
                        })
                      }
                      className="w-full px-4 py-2.5 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 focus:outline-none focus:border-admin-primary"
                    >
                      <option value="active">Active</option>
                      <option value="coming_soon">Coming Soon</option>
                      <option value="deprecated">Deprecated</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      <Palette className="w-4 h-4 inline mr-1" />
                      Theme Colors
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-stone-500 mb-1 block">Primary</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                            className="w-10 h-10 rounded border-0 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.primaryColor}
                            onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                            className="flex-1 px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 text-sm font-mono focus:outline-none focus:border-admin-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-stone-500 mb-1 block">Secondary</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.secondaryColor}
                            onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                            className="w-10 h-10 rounded border-0 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.secondaryColor}
                            onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                            className="flex-1 px-3 py-2 bg-stone-100 border border-stone-300 rounded-lg text-gray-900 text-sm font-mono focus:outline-none focus:border-admin-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Selected Packages Summary */}
                  {formData.defaultPackages.length > 0 && (
                    <div>
                      <p className="text-xs text-stone-500 mb-2">Selected Packages:</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.defaultPackages.map((pkgId) => (
                          <span
                            key={pkgId}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-admin-primary/20 text-admin-primary rounded cursor-pointer hover:bg-admin-primary/30"
                            onClick={() => togglePackageSelection(pkgId)}
                          >
                            {getPackageName(pkgId)}
                            <X className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Package Selection */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Package Selection
                  </h3>
                  <p className="text-xs text-stone-500">
                    Select packages for this product. Selected packages are automatically assigned to all tenants.
                  </p>

                  {/*
                    The count BEFORE the list, so the internal-only situation is
                    visible without scrolling a 400px scroll box — this is the
                    thing a super admin wants to know before creating a product,
                    not after hunting for grey chips.

                    Counted over what is SELECTED, not over the catalogue: the
                    question is what this product ships with.
                  */}
                  {(() => {
                    const internal = formData.defaultPackages.filter(
                      (id) => packages.find((p) => p.id === id)?.marketplace !== true
                    );
                    if (internal.length === 0) return null;
                    return (
                      <div className="rounded-lg border border-stone-300 bg-stone-100 p-2.5 flex items-start gap-2">
                        <Lock className="w-3.5 h-3.5 text-stone-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-stone-600">
                          <span className="font-semibold text-stone-800">
                            {internal.length} internal-only package{internal.length === 1 ? '' : 's'} selected
                          </span>{' '}
                          — {internal.map((id) => getPackageName(id)).join(', ')}. Tenants get these with the
                          product, but cannot add them on their own later. Change that in Packages.
                        </p>
                      </div>
                    );
                  })()}

                  <div className="bg-stone-100 rounded-lg border border-stone-300 overflow-hidden">
                    <div className="max-h-[400px] overflow-y-auto">
                      {packages.length === 0 ? (
                        <div className="p-4 text-center text-stone-500 text-sm">
                          No packages available. Create packages first.
                        </div>
                      ) : (
                        packages.map((pkg) => {
                          const isSelected = formData.defaultPackages.includes(pkg.id);

                          return (
                            <div
                              key={pkg.id}
                              className={`p-3 border-b border-stone-200 last:border-b-0 ${
                                isSelected ? 'bg-stone-200/50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex flex-col gap-1 pt-0.5">
                                  <button
                                    type="button"
                                    onClick={() => togglePackageSelection(pkg.id)}
                                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                      isSelected
                                        ? 'bg-admin-primary border-admin-primary'
                                        : 'border-stone-300 hover:border-stone-400'
                                    }`}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </button>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900 text-sm">{pkg.name}</span>
                                    {pkg.isCore && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
                                        Core
                                      </span>
                                    )}
                                    {/*
                                      SHOWN, NEVER ENFORCED HERE.

                                      Internal-only is about self-service —
                                      whether a tenant can add the package
                                      themselves — and a product's package set
                                      is assigned by a super admin, which is
                                      exactly the supervised path the flag
                                      leaves open. So the row is labelled and
                                      stays fully selectable; the label is here
                                      so nobody assumes a package they picked
                                      will also be buyable on its own.
                                    */}
                                    {pkg.marketplace !== true && (
                                      <span
                                        className="text-[10px] px-1.5 py-0.5 bg-stone-200 text-stone-700 rounded inline-flex items-center gap-1"
                                        title="Not on the marketplace — tenants cannot add this themselves. Change it in Packages."
                                      >
                                        <Lock className="w-2.5 h-2.5" /> Internal
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">
                                    {pkg.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* THE MONTHLY VALUE OF WHAT IS SELECTED — outside the scroll
                      box, so it is readable without scrolling to the end of a
                      400px list, and it updates as packages are ticked. */}
                  {(() => {
                    const total = packageMonthlyTotal(formData.defaultPackages, packages);
                    const priced = formData.defaultPackages.length - total.unpricedCount - total.missingCount;
                    return (
                      <div className="rounded-lg border border-admin-primary/30 bg-admin-primary/5 p-3">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-[10px] uppercase tracking-[.08em] text-stone-500 font-bold">
                              Packages · monthly value
                            </p>
                            <p className="text-[11px] text-stone-500 mt-0.5">
                              {priced} priced package{priced === 1 ? '' : 's'} at list, before any deal discount
                            </p>
                          </div>
                          <span className="text-2xl font-bold tabular-nums text-gray-900">
                            {money(total.cents)}
                            <span className="text-xs font-normal text-stone-500">/mo</span>
                          </span>
                        </div>
                        {total.unpricedCount > 0 && (
                          <p className="text-[11px] text-amber-700 mt-1.5">
                            {total.unpricedCount} selected package{total.unpricedCount === 1 ? ' has' : 's have'} no
                            base price and add{total.unpricedCount === 1 ? 's' : ''} nothing above. Price
                            {total.unpricedCount === 1 ? ' it' : ' them'} in Packages.
                          </p>
                        )}
                        {total.missingCount > 0 && (
                          <p className="text-[11px] text-red-700 mt-1">
                            {total.missingCount} selected id{total.missingCount === 1 ? '' : 's'} no longer
                            exist{total.missingCount === 1 ? 's' : ''} in the packages catalog and contribute
                            {total.missingCount === 1 ? 's' : ''} nothing. Removing
                            {total.missingCount === 1 ? ' it' : ' them'} above and saving clears
                            {total.missingCount === 1 ? ' it' : ' them'}.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  <div className="text-xs text-stone-500">
                    <p>Selected packages are automatically synced to all tenants of this product.</p>
                  </div>
                </div>
              </div>
            </form>

            <div className="flex gap-3 px-5 py-4 border-t border-stone-200 safe-area-bottom">
              <button
                type="button"
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-gray-900 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="flex-1 px-4 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white font-medium rounded-lg transition-colors"
              >
                {isEditing ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffPage>
  );
}

export default ProductsPage;
